export const toFiniteNumber = (value, fallback = null) => {
  if (value === null || value === undefined || value === "") return fallback;
  const number = Number(value);
  return Number.isFinite(number) ? number : fallback;
};

const getChordStart = (chord) =>
  toFiniteNumber(
    chord?.time,
    toFiniteNumber(
      chord?.start,
      toFiniteNumber(chord?.timestamp, toFiniteNumber(chord?.start_time, null))
    )
  );

const getChordEnd = (chord) =>
  toFiniteNumber(chord?.end, toFiniteNumber(chord?.end_time, null));

export const getChordLabel = (item) => {
  if (typeof item === "string") return item.trim() || "--";
  if (!item || typeof item !== "object") return "--";
  const label = item.chord ?? item.name ?? item.label ?? item.chord_name ?? item.symbol;
  if (label === item) return "--";
  return typeof label === "string" ? label.trim() || "--" : getChordLabel(label);
};

export const normalizeChordEvent = (
  event,
  sourceIndex = 0,
  fallbackStart = null,
  fallbackDuration = 4,
  timingSource = null
) => {
  const raw = typeof event === "string" ? { chord: event } : event || {};
  const safeFallbackDuration = Math.max(0.25, toFiniteNumber(fallbackDuration, 4) || 4);
  const start = getChordStart(raw) ?? toFiniteNumber(fallbackStart, sourceIndex * safeFallbackDuration);
  const explicitEnd = getChordEnd(raw);
  const inputDuration = toFiniteNumber(raw.duration, null);
  const duration = inputDuration !== null && inputDuration > 0
    ? inputDuration
    : explicitEnd !== null && explicitEnd > start
      ? explicitEnd - start
      : safeFallbackDuration;
  const end = explicitEnd !== null && explicitEnd > start ? explicitEnd : start + duration;

  return {
    ...raw,
    id: raw.id || `chord-event-${sourceIndex}-${Number(start).toFixed(3)}`,
    chord: getChordLabel(raw),
    start: Math.max(0, start),
    time: Math.max(0, start),
    end: Math.max(0, end),
    duration: Math.max(0.25, duration),
    section: raw.section || raw.section_name || "",
    confidence: raw.confidence ?? null,
    timing_source: raw.timing_source || timingSource || null,
    sourceIndex,
  };
};

const findLastStartIndex = (starts, time) => {
  if (!starts.length || time < starts[0]) return -1;

  let left = 0;
  let right = starts.length - 1;
  let best = -1;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    if (starts[middle] <= time) {
      best = middle;
      left = middle + 1;
    } else {
      right = middle - 1;
    }
  }

  return best;
};

const findFirstStartAfterIndex = (starts, time) => {
  let left = 0;
  let right = starts.length - 1;
  let best = -1;

  while (left <= right) {
    const middle = Math.floor((left + right) / 2);
    if (starts[middle] > time) {
      best = middle;
      right = middle - 1;
    } else {
      left = middle + 1;
    }
  }

  return best;
};

export const buildTimelineStarts = (items = []) =>
  (Array.isArray(items) ? items : []).map((item) => toFiniteNumber(item?.start, 0) || 0);

export const findActiveTimelineIndexAtTime = (
  items = [],
  starts = buildTimelineStarts(items),
  time = 0,
  epsilon = 0.02
) => {
  if (!items.length || !starts.length) return -1;

  const safeTime = toFiniteNumber(time, 0) || 0;
  const candidate = findLastStartIndex(starts, safeTime + epsilon);
  if (candidate < 0 || candidate >= items.length) return -1;

  const item = items[candidate];
  const start = toFiniteNumber(item?.start, starts[candidate]);
  const end = toFiniteNumber(item?.end, Number.POSITIVE_INFINITY);

  return safeTime + epsilon >= start && safeTime < end - epsilon ? candidate : -1;
};

export const findNextTimelineIndexAtTime = (
  items = [],
  starts = buildTimelineStarts(items),
  time = 0,
  epsilon = 0.02
) => {
  if (!items.length || !starts.length) return -1;

  const safeTime = toFiniteNumber(time, 0) || 0;
  const activeIndex = findActiveTimelineIndexAtTime(items, starts, safeTime, epsilon);
  if (activeIndex >= 0) return activeIndex < items.length - 1 ? activeIndex + 1 : -1;

  return findFirstStartAfterIndex(starts, safeTime + epsilon);
};

export const buildChordSyncState = (
  chords = [],
  chordStarts = buildTimelineStarts(chords),
  time = 0,
  _duration = 0,
  epsilon = 0.02
) => {
  const safeTime = toFiniteNumber(time, 0) || 0;
  const activeIndex = findActiveTimelineIndexAtTime(chords, chordStarts, safeTime, epsilon);
  const nextIndex = findNextTimelineIndexAtTime(chords, chordStarts, safeTime, epsilon);
  const activeChord = activeIndex >= 0 ? chords[activeIndex] : null;
  const nextChord = nextIndex >= 0 ? chords[nextIndex] : null;
  const timeToNextChord = nextChord
    ? Math.max(0, Math.round((nextChord.start - safeTime) * 10) / 10)
    : null;

  return {
    activeIndex,
    activeChord,
    nextIndex,
    nextChord,
    timeToNextChord,
  };
};

export const normalizeChordTimeline = (rawChords = [], duration = 0) => {
  const safeDuration = toFiniteNumber(duration, 0) || 0;
  const sorted = (Array.isArray(rawChords) ? rawChords : [])
    .map((chord, sourceIndex) => normalizeChordEvent(chord, sourceIndex))
    .sort((a, b) => a.start - b.start || a.sourceIndex - b.sourceIndex);

  return sorted.map((chord, index) => {
    const nextStart = sorted[index + 1]?.start;
    const derivedEnd =
      chord.end !== null && chord.end > chord.start
        ? chord.end
        : Number.isFinite(nextStart)
          ? nextStart
          : safeDuration > chord.start
            ? safeDuration
            : chord.start + 4;

    return {
      ...chord,
      end: derivedEnd,
      duration: Math.max(0.25, derivedEnd - chord.start),
    };
  });
};

export const normalizeChordChanges = (rawChords = [], duration = 0) => {
  const safeDuration = toFiniteNumber(duration, 0) || 0;
  const normalized = (Array.isArray(rawChords) ? rawChords : [])
    .map((chord, sourceIndex) => normalizeChordEvent(chord, sourceIndex))
    .sort((left, right) => left.start - right.start || left.sourceIndex - right.sourceIndex)
    .filter((event, index, events) => index === 0 || getChordLabel(event) !== getChordLabel(events[index - 1]));

  return normalized.map((event, index) => {
    const nextStart = normalized[index + 1]?.start;
    const end = Number.isFinite(nextStart)
      ? nextStart
      : safeDuration > event.start
        ? safeDuration
        : event.end > event.start
          ? event.end
          : event.start + 4;
    return {
      ...event,
      end,
      duration: Math.max(0.25, end - event.start),
    };
  });
};

export const buildBeatGrid = ({
  duration = 0,
  bpm = 120,
  beatsPerBar = 4,
  subdivision = 1,
} = {}) => {
  const safeBpm = Math.max(1, toFiniteNumber(bpm, 120) || 120);
  const safeBeatsPerBar = Math.max(1, Math.floor(toFiniteNumber(beatsPerBar, 4) || 4));
  const safeSubdivision = Math.max(1, Math.floor(toFiniteNumber(subdivision, 1) || 1));
  const beatDuration = 60 / safeBpm;
  const cellDuration = beatDuration / safeSubdivision;
  const cellCount = Math.max(1, Math.ceil(Math.max(0, toFiniteNumber(duration, 0) || 0) / cellDuration));

  return Array.from({ length: cellCount }, (_, index) => {
    const beatPosition = index / safeSubdivision;
    const wholeBeat = Math.floor(beatPosition);
    return {
      index,
      start: index * cellDuration,
      end: (index + 1) * cellDuration,
      duration: cellDuration,
      barIndex: Math.floor(wholeBeat / safeBeatsPerBar),
      beatInBar: (wholeBeat % safeBeatsPerBar) + 1,
      subdivisionIndex: index % safeSubdivision,
      isBarStart: index % (safeBeatsPerBar * safeSubdivision) === 0,
    };
  });
};

export const getChordChangeAtBeat = (changes = [], beatCell = null) => {
  if (!beatCell) return null;
  return changes.find((change) =>
    change.start >= beatCell.start - 0.0001 && change.start < beatCell.end - 0.0001
  ) || null;
};

export const getActiveChordAtTime = (changes = [], time = 0) => {
  const safeTime = toFiniteNumber(time, 0) || 0;
  let active = null;
  for (const change of changes) {
    if (change.start <= safeTime + 0.02) active = change;
    else break;
  }
  return active;
};

export const getNextChordChange = (changes = [], time = 0) => {
  const safeTime = toFiniteNumber(time, 0) || 0;
  return changes.find((change) => change.start > safeTime + 0.02) || null;
};

export const normalizeSectionTimeline = (rawSections = [], duration = 0) => {
  const safeDuration = toFiniteNumber(duration, 0) || 0;
  return (Array.isArray(rawSections) ? rawSections : [])
    .map((section, sourceIndex) => {
      const start = toFiniteNumber(section?.start, toFiniteNumber(section?.start_time, null));
      const end = toFiniteNumber(section?.end, toFiniteNumber(section?.end_time, null));
      if (start === null || start < 0) return null;

      return {
        ...section,
        name: section?.name || section?.section_name || `Section ${sourceIndex + 1}`,
        start,
        end: end !== null && end > start ? end : safeDuration || start + 8,
        sourceIndex,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.start - b.start || a.sourceIndex - b.sourceIndex);
};

export const findActiveChordIndexAtTime = (chords = [], time = 0, epsilon = 0.02) => {
  return findActiveTimelineIndexAtTime(chords, buildTimelineStarts(chords), time, epsilon);
};

export const findActiveChordAtTime = (chords = [], time = 0, epsilon = 0.02) => {
  const index = findActiveChordIndexAtTime(chords, time, epsilon);
  return index >= 0 ? chords[index] : null;
};

export const findActiveSectionIndexAtTime = (sections = [], time = 0) => {
  return findActiveTimelineIndexAtTime(sections, buildTimelineStarts(sections), time, 0);
};

export const findNextChordAtTime = (chords = [], time = 0, epsilon = 0.02) => {
  const nextIndex = findNextTimelineIndexAtTime(chords, buildTimelineStarts(chords), time, epsilon);
  return nextIndex >= 0 ? chords[nextIndex] : null;
};

export const getTimeToNextChord = (chords = [], time = 0, epsilon = 0.02) => {
  const nextChord = findNextChordAtTime(chords, time, epsilon);
  if (!nextChord) return null;
  return Math.max(0, nextChord.start - (toFiniteNumber(time, 0) || 0));
};
